import { of as observableOf } from 'rxjs';

import { map, withLatestFrom, switchMap, catchError, filter } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { Actions, Effect, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';

import { CAAction } from '../actions';
import * as actions from '../actions/index';
import * as selectors from '../reducers/index';
import { ConfirmationDialogComponent } from '../../shared/confirmation-dialog/confirmation-dialog.component';
import { MatDialog } from '@angular/material/dialog';
import { getAuthenticationState } from '../auth/auth.selectors';
import { ADMIN_ROLE, CAREAPP_MANAGER, CAREAPP_SUPPORT_1, PENDING } from '@careapp-group/core';
import {
  InvitationRequestDto,
  InvitationsService,
  MembersService,
} from '@careapp-group/ng-sdk-v2-new';
import { getCurrentOrganisationTenantId } from '../organisations/organisations.selectors';

@Injectable()
export class InviteEffects {
  @Effect()
  sendInvitation = this.actions$.pipe(
    ofType<CAAction>(actions.REQUEST_INVITATION_SEND),
    withLatestFrom(
      this.store.select(selectors.getSelectedId(selectors.ORGANISATIONS)),
      this.store.select(selectors.getSelectedId(selectors.TENANTS))
    ),
    switchMap(([action, organisationId, tenantId]) => {
      const typedAction = action as actions.RequestInvitationSendAction;
      let isTenantInvitation = typedAction?.payload?.modelName === 'Tenant' ?? false;
      return this.invitesService
        .createInvitation({
          invitationRequestDto: {
            ...typedAction.payload,
            organisationId: isTenantInvitation ? undefined : organisationId,
            tenantId: isTenantInvitation ? tenantId : undefined,
          },
        })
        .pipe(
          map((payload) => new actions.ResponseSuccessInvitationSendAction(payload)),
          catchError((err) => observableOf(new actions.ResponseFailInvitationSendAction(err)))
        );
    })
  );

  @Effect()
  bulkInvite = this.actions$.pipe(
    ofType(actions.Members.REQUEST_BULK_IMPORT),
    withLatestFrom(this.store.select(selectors.getSelectedId(selectors.ORGANISATIONS))),
    switchMap(([action, id]) =>
      this.membersService
        .importMembers({
          organisationId: id,
          invitationRequestDto: [
            ...action.payload.map((request: InvitationRequestDto) => ({
              ...request,
              organisationId: id,
            })),
          ] as InvitationRequestDto[],
        })
        .pipe(
          map((response) => actions.Members.ResponseSuccessBulkImport(response)),
          catchError((err) => observableOf(actions.Members.ResponseFailBulkImport(err)))
        )
    )
  );

  @Effect()
  getPending = this.actions$.pipe(
    ofType(actions.REQUEST_INVITATIONS_PENDING),
    withLatestFrom(
      this.store.select(selectors.getSelectedId(selectors.ORGANISATIONS)),
      this.store.select(selectors.getSelectedId(selectors.TENANTS)),
      this.store.select(getCurrentOrganisationTenantId)
    ),
    switchMap(([action, organisationId, selectedTenant, selectedOrganisationTenant]) => {
      const forModel = (action as any).payload?.modelName;
      return this.invitesService
        .listInvitations({
          organisationId: !forModel || forModel === 'Organisation' ? organisationId : undefined,
          tenantId:
            forModel === 'Tenant' ? selectedTenant ?? selectedOrganisationTenant : undefined,
        })
        .pipe(
          map((response) => new actions.ResponseSuccessPendingInvitations(response)),
          catchError((err) => observableOf(new actions.ResponseFailPendingInvitations(err)))
        );
    })
  );

  @Effect()
  searchAllPending = this.actions$.pipe(
    ofType<CAAction>(actions.REQUEST_SEARCH_ALL_PENDING_INVITATIONS_ADMIN),
    withLatestFrom(this.store.select(getAuthenticationState)),
    filter(
      ([action, auth]) =>
        !!(
          auth.roles &&
          auth.roles.find(
            (value) =>
              value === ADMIN_ROLE || value === CAREAPP_SUPPORT_1 || value === CAREAPP_MANAGER
          )
        )
    ),
    switchMap(([action, auth]) => {
      return this.invitesService
        .listInvitations({
          organisationId: null,
          tenantId: null,
          to: action.payload.query,
          status: PENDING,
        })
        .pipe(
          map((result) => new actions.ResponseSuccessSearchPendingInvitationsAdmin(result)),
          catchError((err) =>
            observableOf(new actions.ResponseFailSearchPendingInvitationsAdmin(err))
          )
        );
    })
  );
  @Effect()
  resendInvite = this.actions$.pipe(
    ofType<CAAction>(actions.REQUEST_INVITATION_RESEND),
    switchMap((action) =>
      this.invitesService.resendInvitation({ id: action.payload.id }).pipe(
        map((payload) => new actions.ResponseSuccessInvitationResendAction(payload)),
        catchError((err) =>
          observableOf(new actions.ResponseFailInvitationResendAction(action.payload, err))
        )
      )
    )
  );

  @Effect()
  cancelInvite = this.actions$.pipe(
    ofType(actions.REQUEST_INVITATION_CANCEL),
    switchMap((action) =>
      this.dialog
        .open(ConfirmationDialogComponent, {
          data: {
            title: 'Cancel Invitation?',
            body: 'Are you sure you want to cancel this invitation?',
            confirm: 'Cancel invite',
            cancel: 'Keep invite',
            confirmColor: 'warn',
          },
        })
        .afterClosed()
        .pipe(map((result) => [action, result]))
    ),
    switchMap(([action, result]) =>
      result
        ? this.invitesService.cancelInvitation({ id: action.payload.id }).pipe(
            map(
              (payload) =>
                new actions.ResponseSuccessInvitationCancelAction({
                  id: action.payload.id,
                })
            ),
            catchError((err) => observableOf(new actions.ResponseFailInvitationCancel(err)))
          )
        : observableOf({ type: 'CANCELLED' })
    )
  );

  getInviteWithToken$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.RequestInviteWithToken),
      switchMap((action) =>
        this.invitesService.getInvitationDetails({ token: action.token }).pipe(
          map((payload) => actions.SuccessInviteWithToken({ payload })),
          catchError((err) => observableOf(actions.FailInviteWithToken({ error: err })))
        )
      )
    )
  );
  acceptInvite$ = createEffect(() =>
    this.actions$.pipe(
      ofType(actions.RequestAcceptInvite),
      switchMap((action) =>
        this.invitesService.acceptInvitation({ token: action.token }).pipe(
          map(() => actions.SuccessAcceptInvite()),
          catchError((err) => observableOf(actions.FailAcceptInvite({ error: err })))
        )
      )
    )
  );

  constructor(
    private store: Store<any>,
    private actions$: Actions<CAAction>,
    private invitesService: InvitationsService,
    private membersService: MembersService,
    private dialog: MatDialog
  ) {}
}
